import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import { useApiKey } from '../context/ApiKeyContext';
import { generateFeedback } from '../utils/anthropicService';

const Container = styled.div`
  padding: 2rem;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-width: 800px;
  width: 100%;
`;

const Title = styled.h2`
  color: #0d9488;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const Subtitle = styled.h3`
  color: #333;
  margin-top: 2rem;
  margin-bottom: 1rem;
  font-size: 1.2rem;
`;

const FeedbackContent = styled.div`
  background-color: #f9f9f9;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 2rem;
  line-height: 1.6;

  > *:first-child {
    margin-top: 0;
  }

  > *:last-child {
    margin-bottom: 0;
  }

  p, ul, ol, blockquote, pre {
    margin: 0 0 1rem;
  }

  h1, h2, h3, h4, h5, h6 {
    margin: 2rem 0 1rem;
  }

  hr {
    margin: 1.5rem 0;
    border: none;
    border-top: 1px solid #ddd;
  }

  ul, ol {
    padding-left: 1.5rem;
  }

  li {
    margin-bottom: 0.25rem;
  }

  code {
    background-color: #eee;
    padding: 0.15rem 0.3rem;
    border-radius: 3px;
    font-size: 0.9em;
  }
`;

const ConversationPreview = styled.div`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 2rem;
  border: 1px solid #eee;
  border-radius: 8px;
  padding: 1rem;
`;

const MessageItem = styled.div`
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const MessageHeader = styled.div`
  font-weight: 600;
  color: ${({ isUser }) => (isUser ? '#0d9488' : '#333')};
  margin-bottom: 0.5rem;
`;

const MessageContent = styled.div`
  margin-left: 1rem;
  color: #555;
  white-space: pre-wrap;
`;

// A pause the user let happen in realistic mode.
const SilenceItem = styled.div`
  font-style: italic;
  color: #999;
  font-size: 0.9rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: ${({ $secondary }) => ($secondary ? 'white' : '#0d9488')};
  color: ${({ $secondary }) => ($secondary ? '#0d9488' : 'white')};
  border: ${({ $secondary }) => ($secondary ? '1px solid #0d9488' : 'none')};
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: ${({ $secondary }) => ($secondary ? '#f0fdfa' : '#0f766e')};
  }
`;

const LoadingText = styled.div`
  text-align: center;
  color: #666;
  padding: 2rem;
  font-style: italic;
`;

// How the conversation closed. An ending is not the same as a success, so
// "damaged" gets its own colour rather than being lumped in with the rest.
const OUTCOME_STYLES = {
  resolved: { bg: '#e8f5e9', fg: '#2e7d32' },
  unresolved: { bg: '#fff8e1', fg: '#8d6e00' },
  damaged: { bg: '#fdecea', fg: '#c62828' },
};

const OutcomeBanner = styled.div`
  text-align: center;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  font-weight: 600;
  background-color: ${({ $outcome }) => (OUTCOME_STYLES[$outcome] || OUTCOME_STYLES.unresolved).bg};
  color: ${({ $outcome }) => (OUTCOME_STYLES[$outcome] || OUTCOME_STYLES.unresolved).fg};
`;

const FeedbackDisplay = ({ conversation, character, scenario, conclusion, conversationId, onStartOver, onTryAgain, onTrialBlocked }) => {
  const { apiKey, trialMode } = useApiKey();
  const [feedback, setFeedback] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const auth = trialMode ? { trial: true } : { apiKey };
    const fetchFeedback = async () => {
      try {
        setIsLoading(true);
        const result = await generateFeedback(auth, scenario, character, conversation, { conversationId, conclusion });

        if (result.success) {
          setFeedback(result.feedback);
        } else if ((result.code === 'owner_out_of_credits' || result.code === 'trial_exhausted') && onTrialBlocked) {
          onTrialBlocked(result.code);
        } else if (result.code === 'turn_limit') {
          setFeedback('This conversation ran past its length limit, so feedback is unavailable for it. Try a shorter practice run.');
        } else {
          setFeedback('Unable to generate feedback at this time. Please try again later.');
        }
      } catch (error) {
        console.error('Error generating feedback:', error);
        setFeedback('An error occurred while generating feedback. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeedback();
  }, [apiKey, trialMode, conversation, scenario, character, conclusion, conversationId, onTrialBlocked]);

  return (
    <Container>
      <Title>Your Coaching Feedback</Title>

      {conclusion && (
        <OutcomeBanner $outcome={conclusion.outcome}>
          {conclusion.endReason || 'The conversation came to an end.'}
        </OutcomeBanner>
      )}

      <Subtitle>{scenario.title} (with {character.name})</Subtitle>
      <ConversationPreview>
        {conversation.length === 0 ? (
          <MessageContent>No conversation to review yet.</MessageContent>
        ) : (
          conversation.map((message, index) => (
            <MessageItem key={index}>
              {message.kind === 'silence' ? (
                <SilenceItem>{message.note}</SilenceItem>
              ) : (
                <>
                  <MessageHeader isUser={message.role === 'user'}>
                    {message.role === 'user' ? 'You' : character.name}
                  </MessageHeader>
                  <MessageContent>{message.content}</MessageContent>
                </>
              )}
            </MessageItem>
          ))
        )}
      </ConversationPreview>

      {isLoading ? (
        <LoadingText>Analyzing your conversation and writing your feedback...</LoadingText>
      ) : (
        <FeedbackContent>
          <ReactMarkdown>{feedback}</ReactMarkdown>
        </FeedbackContent>
      )}

      <ButtonContainer>
        <Button onClick={onStartOver} $secondary={true}>
          Choose Another Scenario
        </Button>
        <Button onClick={onTryAgain}>
          Try This Again
        </Button>
      </ButtonContainer>
    </Container>
  );
};

export default FeedbackDisplay;
